import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Alert,
  Tabs,
  ScrollArea,
  Badge,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconDeviceFloppy,
  IconSettings,
  IconCopy,
  IconLayersLinked,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import {
  BaseSchema,
  TemplateDoc,
  RecordWithMetadata,
  BaseSchemaSchema,
  TemplateDocSchema,
  CreateGenevaRequestData,
  GenevaSupportingData,
  GenevaSupportingFieldMap,
  GenevaSupportingItemSummary,
} from '../../../types/geneva-schema';
import { useGenevaSchemaService } from '../../settings/hooks/useGenevaSchemaService';
import { useGenevaRequestService } from '../../settings/hooks/useGenevaRequestService';
import { GenevaRecordForm } from './GenevaRecordForm';
import { MyUserDataContext } from '../../../contexts';
import { resolveFieldOverridesRecursively, resolveMultiRecordFieldOverrides, generateCompleteGenevaObject, validateRecordData, validateMultiRecordData } from '../../../utils/geneva-schema-helpers';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from '../../../helpers/firebaseContext';
import { createPortfolioValidationFunctions } from '../../../../../shared/geneva-functions';
import { loadBaseSchema, applyDefaultHiddenOverrides } from '../../../utils/geneva-schema-loader';
import { GenevaFieldRenderer } from '../../../components/GenevaFieldRenderer';
import { GenevaApiItem } from '../../../hooks/useGenevaLookup';
import { normalizeSupportingRecords as normalizeSupportingRecordsUtil } from '../../../utils/geneva-supporting-data';
import { SchemaType } from '../../../types/schema';
import { DEFAULT_SCHEMA_TYPE } from '../../../utils/schema-config';
import { getSchemaDefaults } from '../../../utils/geneva-schema-loader';

interface GenevaFormGeneratorProps {
  orgId: string;
  templateId: string;
  requestId?: string; // If editing existing request
  onSave?: (requestId: string) => void | Promise<void>;
  readonly?: boolean;
  schemaType?: SchemaType;
}

const getRecordDisplayLabel = (record: RecordWithMetadata): string => {
  if (!record) return '';
  if (record.isVirtual) {
    return record.label || record.record || '';
  }
  return record.record || '';
};

export const GenevaFormGenerator: React.FC<GenevaFormGeneratorProps> = ({
  orgId,
  templateId,
  requestId,
  onSave,
  readonly = false,
  schemaType = DEFAULT_SCHEMA_TYPE,
}) => {
  const userContext = useContext(MyUserDataContext);
  const functions = useFunctions();
  const [schema, setSchema] = useState<BaseSchema | null>(null);
  const [template, setTemplate] = useState<TemplateDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [supportingData, setSupportingData] = useState<Record<string, GenevaSupportingFieldMap>>({});
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const schemaDefaults = useMemo(() => getSchemaDefaults(schemaType), [schemaType]);
  const schemaLabel = schemaDefaults.displayName;

  useEffect(() => {
    setSchema(null);
    setTemplate(null);
    setFormData({});
    setSupportingData({});
    setSelectedRecord(null);
  }, [schemaType, templateId]);

  // Set up async validation functions
  const asyncValidationFunctions = useMemo(() => {
    if (!functions) return undefined;

    // Create Firebase callable functions
    const firebaseFunctions = {
      getPortfolioPositionWithCustodianAccounts: httpsCallable(functions, 'getPortfolioPositionWithCustodianAccounts'),
    };

    // Use the unified function to create validation functions for client-side
    return createPortfolioValidationFunctions(firebaseFunctions);
  }, [functions]);

  const genevaService = useGenevaSchemaService({
    orgId,
    templateId,
    userId: userContext.user?.uid || 'form-user',
    userEmail: userContext.user?.email || 'form-user@example.com',
    schemaType,
  });
  const { loadTemplate: loadSchemaTemplate } = genevaService;

  const {
    getRequest,
    createRequest: createGenevaRequest,
    updateRequestData: updateGenevaRequestData,
    submitForApproval: submitGenevaRequestForApproval,
  } = useGenevaRequestService({
    orgId,
    userId: userContext.user?.uid || 'form-user',
    userEmail: userContext.user?.email || 'form-user@example.com',
    userDisplayName: userContext.user?.displayName || undefined,
    schemaType,
  });

  const sanitizeSupportingItem = useCallback((item: GenevaApiItem | null): GenevaSupportingItemSummary | null => {
    if (!item) return null;

    const summary: GenevaSupportingItemSummary = {};
    const code = item.CODE ?? item.NAMESORT ?? item.MAKENAME ?? null;
    const description = item.DESCRIPTION ?? item.OBJECTDESCRIPTION ?? null;
    const name = (item as any).NAMELINE1 ?? item.OBJECTDESCRIPTION ?? null;

    if (code) summary.code = code;
    if (description) summary.description = description;
    if (name) summary.name = name;

    try {
      summary.rawJson = JSON.stringify(item);
    } catch (err) {
      console.warn('Failed to serialize supporting item', err);
    }

    return Object.keys(summary).length > 0 ? summary : null;
  }, []);

  const normalizeLoadedSupportingRecords = useCallback((raw: GenevaSupportingData | undefined | null): Record<string, GenevaSupportingFieldMap> => {
    const normalized = normalizeSupportingRecordsUtil(raw);

    return Object.entries(normalized).reduce((acc, [recordName, fields]) => {
      if (!fields || typeof fields !== 'object') {
        return acc;
      }

      const sanitizedFields = Object.entries(fields as Record<string, any>).reduce<GenevaSupportingFieldMap>((fieldAcc, [fieldName, value]) => {
        let summary: GenevaSupportingItemSummary | null = null;

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const candidate = value as GenevaSupportingItemSummary;
          if ('code' in candidate || 'description' in candidate || 'name' in candidate || 'rawJson' in candidate) {
            summary = {
              code: candidate.code ?? null,
              description: candidate.description ?? null,
              name: candidate.name ?? null,
              rawJson: candidate.rawJson,
            };
          } else {
            summary = sanitizeSupportingItem(value as GenevaApiItem);
          }
        } else if (typeof value === 'string') {
          summary = { rawJson: value };
        }

        if (summary) {
          fieldAcc[fieldName] = summary;
        }
        return fieldAcc;
      }, {} as GenevaSupportingFieldMap);

      if (Object.keys(sanitizedFields).length > 0) {
        acc[recordName] = sanitizedFields;
      }

      return acc;
    }, {} as Record<string, GenevaSupportingFieldMap>);
  }, [sanitizeSupportingItem]);

  // Load schema and template - use stable dependencies
  useEffect(() => {
    let mounted = true;

    const loadSchemaAndTemplate = async () => {
      try {
        if (!mounted) return;
        setLoading(true);
        setError(null);

        // Load schema (cache first, with network fallback)
        const schemaData = await loadBaseSchema({
          schemaType,
          url: schemaDefaults.schemaUrl,
          cacheKey: schemaDefaults.cacheKey,
        });
        if (mounted) setSchema(schemaData);

        // Load template
        const templateData = await loadSchemaTemplate();

        // Apply default hidden overrides centrally
        const modifiedTemplate = applyDefaultHiddenOverrides(templateData);
        if (mounted) setTemplate(modifiedTemplate);

      } catch (err) {
        console.error('Failed to load schema/template:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load form configuration');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSchemaAndTemplate();

    return () => {
      mounted = false;
    };
  }, [schemaType, schemaDefaults, orgId, templateId, loadSchemaTemplate]);

  // Load existing request data when editing
  useEffect(() => {
    let mounted = true;
    if (!requestId) return;

    const loadExisting = async () => {
      try {
        const existing = await getRequest(requestId!);
        if (!mounted || !existing) return;

        const saved: any = (existing as any).formData || {};
        const savedSupporting: any = (existing as any).supportingData || {};
        if (saved && saved._multiRecord && saved.records) {
          // Multi-record: records object contains per-record sections keyed by `${virtualId}__${recordName}`
          setFormData(saved.records);
          if (savedSupporting && savedSupporting.records) {
            setSupportingData(normalizeLoadedSupportingRecords(savedSupporting.records as GenevaSupportingData));
          } else {
            setSupportingData({});
          }
          const virtualId = saved._virtualRecordId || existing.metadata?.virtualRecordId;
          if (virtualId) setSelectedRecord(virtualId);
        } else if (saved && typeof saved === 'object') {
          // Single-record: map of { [recordName]: { ...fields } }
          setFormData(saved);
          if (savedSupporting && typeof savedSupporting === 'object' && !Array.isArray(savedSupporting)) {
            // Handle case where supporting data was stored in multi-record wrapper by falling back to records map
            if ((savedSupporting as any)._multiRecord && savedSupporting.records) {
              setSupportingData(normalizeLoadedSupportingRecords(savedSupporting.records as GenevaSupportingData));
            } else {
              setSupportingData(normalizeLoadedSupportingRecords(savedSupporting as GenevaSupportingData));
            }
          } else {
            setSupportingData({});
          }
          const keys = Object.keys(saved).filter(k => !k.startsWith('_') && k !== 'records');
          if (existing.metadata?.recordType && saved[existing.metadata.recordType]) {
            setSelectedRecord(existing.metadata.recordType);
          } else if (keys.length > 0) {
            setSelectedRecord(keys[0]);
          }
        }
      } catch (err) {
        console.error('Failed to load existing request data:', err);
        if (mounted) setError(prev => prev || 'Failed to load existing request data');
      }
    };

    loadExisting();
    return () => { mounted = false; };
  }, [requestId, getRequest, normalizeLoadedSupportingRecords]);

  // Get available records (real + virtual, only enabled ones)
  const availableRecords = useMemo((): RecordWithMetadata[] => {
    if (!schema || !template) return [];

    const records: RecordWithMetadata[] = [];

    // Real records
    schema.records.forEach(record => {
      if (record && record.record && template.enabledRecords[record.record]) {
        const fieldOverrides = template.fieldOverrides[record.record] || {};
        records.push({
          ...record,
          enabled: true,
          fieldCount: record.fields?.length || 0,
          overriddenFieldCount: Object.keys(fieldOverrides).length,
          isVirtual: false,
        });
      }
    });

    // Virtual records
    if (template.virtualRecords) {
      Object.entries(template.virtualRecords).forEach(([recordId, virtualRecord]) => {
        if (virtualRecord && virtualRecord.enabled && recordId) {
          // Handle multi-record virtual records
          if (virtualRecord.isMultiRecord && virtualRecord.baseRecords) {
            // For multi-record, we add it as a special entry that combines all records
            const allFields: any[] = [];
            let totalFieldCount = 0;

            // Validate that all base records exist
            const allBaseRecordsExist = virtualRecord.baseRecords.every(baseRecordName =>
              schema.records.find(r => r && r.record === baseRecordName)
            );

            if (allBaseRecordsExist) {
              virtualRecord.baseRecords.forEach(baseRecordName => {
                const baseRecord = schema.records.find(r => r && r.record === baseRecordName);
                if (baseRecord && baseRecord.fields) {
                  allFields.push(...baseRecord.fields);
                  totalFieldCount += baseRecord.fields.length;
                }
              });

              records.push({
                record: recordId,
                fields: allFields,
                enabled: true,
                fieldCount: totalFieldCount,
                overriddenFieldCount: virtualRecord.multiRecordConfig?.reduce((count, config) =>
                  count + Object.keys(config.fieldOverrides || {}).length, 0) || 0,
                isVirtual: true,
                isMultiRecord: true,
                baseRecords: virtualRecord.baseRecords,
                label: virtualRecord.label || recordId,
              });
            }
          } else if (virtualRecord.base) {
            // Handle single-record virtual records (backward compatibility)
            // For recursive inheritance, find the ultimate base record
            let ultimateBaseRecord = null;
            let currentRecordId = virtualRecord.base;

            // Walk the chain to find the ultimate base record
            while (currentRecordId) {
              const realRecord = schema.records.find(r => r && r.record === currentRecordId);
              if (realRecord) {
                ultimateBaseRecord = realRecord;
                break;
              }

              // Check if it's a virtual record
              const virtualBase = template.virtualRecords?.[currentRecordId];
              if (virtualBase?.base) {
                currentRecordId = virtualBase.base;
              } else {
                break;
              }
            }

            if (ultimateBaseRecord && ultimateBaseRecord.fields) {
              // Count total overrides using recursive resolution
              const resolvedOverrides = resolveFieldOverridesRecursively(recordId, template);

              records.push({
                record: recordId,
                fields: ultimateBaseRecord.fields,
                enabled: true,
                fieldCount: ultimateBaseRecord.fields.length,
                overriddenFieldCount: Object.keys(resolvedOverrides).length,
                isVirtual: true,
                baseRecord: virtualRecord.base, // Keep immediate parent for display
                label: virtualRecord.label || recordId,
              });
            }
          }
        }
      });
    }

    const filteredRecords = records.filter(record => record && record.record); // Final safety filter
    const sortedRecords = filteredRecords
      .slice()
      .sort((a, b) => getRecordDisplayLabel(a).localeCompare(
        getRecordDisplayLabel(b),
        undefined,
        { sensitivity: 'base' }
      ));
    console.log('Available records:', sortedRecords);
    return sortedRecords;
  }, [schema, template]);

  // Auto-select first record if none selected
  useEffect(() => {
    if (availableRecords.length > 0 && !selectedRecord) {
      setSelectedRecord(availableRecords[0].record);
    }
  }, [availableRecords, selectedRecord]);

  const handleFieldChange = useCallback((recordName: string, fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [recordName]: {
        ...prev[recordName],
        [fieldName]: value,
      },
    }));
  }, []);

  const handleSupportingDataChange = useCallback((recordName: string, fieldName: string, item: GenevaApiItem | null) => {
    const sanitized = sanitizeSupportingItem(item);
    setSupportingData(prev => {
      const updatedRecord = { ...(prev[recordName] || {}) };

      if (!sanitized) {
        delete updatedRecord[fieldName];
      } else {
        updatedRecord[fieldName] = sanitized;
      }

      if (Object.keys(updatedRecord).length === 0) {
        const { [recordName]: _removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [recordName]: updatedRecord,
      };
    });
  }, [sanitizeSupportingItem]);

  // Handle record type change - clear form data when switching records
  const handleRecordChange = useCallback((newRecord: string | null) => {
    if (newRecord !== selectedRecord) {
      // Clear all form data when switching record types
      setFormData({});
      setSupportingData({});
    }
    setSelectedRecord(newRecord);
  }, [selectedRecord]);

  const handleSaveRequest = useCallback(async (submitForApproval: boolean = false) => {
    try {
      setSaving(true);

      // Determine the record type and data structure
      const selectedRecordData = selectedRecord ? availableRecords.find(r => r.record === selectedRecord) : null;
      if (!selectedRecordData) {
        throw new Error('No record selected');
      }

      let dataToSave = formData;
      const hasSupportingData = Object.keys(supportingData || {}).length > 0;
      let supportingDataToSave: GenevaSupportingData | undefined = hasSupportingData ? supportingData : undefined;
      let recordType = selectedRecordData.record;
      let multiTransactionsCount = 0;

      if (selectedRecordData.isMultiRecord) {
        // For multi-record forms, organize data by actual record type
        dataToSave = {
          _multiRecord: true,
          _virtualRecordId: selectedRecord,
          records: formData,
        };
        supportingDataToSave = hasSupportingData
          ? {
            _multiRecord: true,
            ...(selectedRecord ? { _virtualRecordId: selectedRecord } : {}),
            records: supportingData,
          }
          : undefined;
        recordType = selectedRecordData.label || selectedRecord!;
        // Validate multi-record data client-side
        if (schema && template && selectedRecord) {
          const v = await validateMultiRecordData(selectedRecord, formData, template, schema, {
            asyncFunctions: asyncValidationFunctions,
          });
          const fieldErrors = Object.values(v.results)
            .flatMap(r => Object.entries(r.fieldErrors)
              .flatMap(([f, msgs]) => msgs.map(m => `${f}: ${m}`)));
          const recordErrors = Object.values(v.results).flatMap(r => r.recordErrors);
          const allErrors = [...fieldErrors, ...recordErrors];
          if (v.warnings.length > 0) {
            console.warn('Validation warnings:', v.warnings);
          }
          if (allErrors.length > 0) {
            throw new Error(`Validation failed:\n- ${allErrors.join('\n- ')}`);
          }
        }
      }
      else if (schema && template && selectedRecord) {
        // Validate single record data client-side (including additional transactions if any)
        const baseData = formData[selectedRecord] || {};

        // Resolve overrides to determine per-transaction fields
        const resolvedOverrides = resolveFieldOverridesRecursively(selectedRecord, template);
        const recordDef = schema.records.find(r => r.record === selectedRecord);
        const perTxnFields = (recordDef?.fields || [])
          .filter(field => {
            const override = resolvedOverrides[field.xmlTag];
            if (override?.multiTxn !== true) {
              return false;
            }

            if (field.quickImportColumn && typeof field.quickImportColumn === 'string') {
              const firstLine = field.quickImportColumn.split('\n')[0].trim();
              if (firstLine.includes('.') && !firstLine.includes(' ') && !firstLine.startsWith('Note:')) {
                return false;
              }
            }

            return !field.xmlTag.includes('.');
          });

        // Validate base transaction
        const vBase = await validateRecordData(selectedRecord, baseData, template, schema, undefined, {
          asyncFunctions: asyncValidationFunctions,
        });
        const baseErrors = [
          ...Object.entries(vBase.fieldErrors).flatMap(([f, msgs]) => msgs.map(m => `${f}: ${m}`)),
          ...vBase.recordErrors,
        ];
        if (vBase.warnings.length > 0) {
          console.warn('Validation warnings (base):', vBase.warnings);
        }
        if (baseErrors.length > 0) {
          throw new Error(`Validation failed:\n- ${baseErrors.join('\n- ')}`);
        }

        // Validate additional transactions if present
        const extras: any[] = Array.isArray(formData._multiTransactions) ? formData._multiTransactions : [];
        multiTransactionsCount = 1 + (extras?.length || 0);
        if (extras.length > 0) {
          const allExtraErrors: string[] = [];
          for (let i = 0; i < extras.length; i++) {
            const item = extras[i] || {};
            // Merge base + per-transaction overrides for validation
            const merged = { ...baseData } as Record<string, any>;
            for (const f of perTxnFields) {
              if (item[f.xmlTag] !== undefined) merged[f.xmlTag] = item[f.xmlTag];
            }
            const vExtra = await validateRecordData(selectedRecord, merged, template, schema, undefined, {
              asyncFunctions: asyncValidationFunctions,
            });
            const errs = [
              ...Object.entries(vExtra.fieldErrors).flatMap(([f, msgs]) => msgs.map(m => `Tx ${i + 2} – ${f}: ${m}`)),
              ...vExtra.recordErrors.map(m => `Tx ${i + 2} – ${m}`),
            ];
            if (errs.length > 0) allExtraErrors.push(...errs);
          }
          if (allExtraErrors.length > 0) {
            throw new Error(`Validation failed:\n- ${allExtraErrors.join('\n- ')}`);
          }
        }
      }

      // Count fields and array fields
      const fieldCount = Object.keys(dataToSave).length;
      const arrayFieldCount = Object.values(dataToSave).filter(value => Array.isArray(value)).length;

      const metadata: any = {
        recordType,
        isMultiRecord: selectedRecordData.isMultiRecord || false,
        fieldCount,
        arrayFieldCount,
      };

      // Only add virtualRecordId if it's a multi-record
      if (selectedRecordData.isMultiRecord) {
        metadata.virtualRecordId = selectedRecord!;
      }
      // For single-record with additional transactions, add a hint
      if (!selectedRecordData.isMultiRecord && Array.isArray(formData._multiTransactions)) {
        metadata.multiTransactionsCount = 1 + formData._multiTransactions.length;
      }

      const requestData: CreateGenevaRequestData = {
        orgId,
        templateId,
        schemaType,
        formData: dataToSave,
        metadata,
        supportingData: supportingDataToSave,
      };

      let finalRequestId: string;

      if (requestId) {
        // Update existing request
        await updateGenevaRequestData(requestId, dataToSave, supportingDataToSave);
        finalRequestId = requestId;

        if (submitForApproval) {
          await submitGenevaRequestForApproval(requestId);
        }
      } else {
        // Create new request
        finalRequestId = await createGenevaRequest(requestData);

        if (submitForApproval) {
          await submitGenevaRequestForApproval(finalRequestId);
        }
      }

      notifications.show({
        title: 'Success',
        message: submitForApproval
          ? `${schemaLabel} request submitted for approval successfully`
          : `${schemaLabel} request saved successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Call onSave callback with the request ID for navigation
      if (onSave) {
        await onSave(finalRequestId);
      }

      return finalRequestId;
    } catch (error) {
      console.error('Failed to save request:', error);
      notifications.show({
        title: 'Save Failed',
        message: error instanceof Error ? error.message : `Failed to save ${schemaLabel} request`,
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    formData,
    supportingData,
    onSave,
    selectedRecord,
    availableRecords,
    orgId,
    templateId,
    requestId,
    updateGenevaRequestData,
    submitGenevaRequestForApproval,
    createGenevaRequest,
    schemaType,
    schemaLabel,
  ]);

  const handleGenerateCompleteObject = useCallback(() => {
    if (!selectedRecord || !schema || !template) {
      notifications.show({
        title: 'Error',
        message: 'Please select a record first',
        color: 'red',
      });
      return;
    }

    try {
      const currentFormData = formData[selectedRecord] || {};
      const completeObject = generateCompleteGenevaObject(
        selectedRecord,
        currentFormData,
        template,
        schema,
        undefined, // recordScopes
        requestId || undefined // Use requestId if editing, undefined for new requests
      );

      console.log(`Complete ${schemaLabel} Object:`, completeObject);
      console.log(`Complete ${schemaLabel} Object (JSON):`, JSON.stringify(completeObject, null, 2));

      notifications.show({
        title: 'Complete Object Generated',
        message: `Check the browser console for the complete ${schemaLabel} XML-ready object`,
        color: 'blue',
      });
    } catch (error) {
      console.error('Error generating complete object:', error);
      notifications.show({
        title: 'Generation Failed',
        message: error instanceof Error ? error.message : 'Failed to generate complete object',
        color: 'red',
      });
    }
  }, [selectedRecord, schema, template, formData, schemaLabel]);

  if (loading) {
    return (
      <Box pos="relative" h={400}>
        <LoadingOverlay visible />
        <Text ta="center" mt="xl">Loading {schemaLabel} form configuration...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Configuration Error"
        color="red"
      >
        {error}
        <Text size="sm" mt="xs">
          Please configure your {schemaLabel} schema in Settings → Schema Configurator first.
        </Text>
      </Alert>
    );
  }

  if (availableRecords.length === 0) {
    return (
      <Alert
        icon={<IconSettings size={16} />}
        title="No Records Enabled"
        color="blue"
      >
        No records are enabled in your template. Please enable records in Settings → Schema Configurator first.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={3}>{schemaLabel} Request Form</Title>
          {/* <Text size="sm" c="dimmed">
            Template: {template?.name} • {availableRecords.length} available record types
          </Text> */}
        </Box>

        {!readonly && (
          <Group gap="sm">
            {/* <Button
              onClick={handleGenerateCompleteObject}
              variant="light"
              leftSection={<IconCopy size={16} />}
            >
              Generate Complete Object
            </Button>
            <Button
              onClick={() => handleSaveRequest(false)}
              loading={saving}
              leftSection={<IconDeviceFloppy size={16} />}
              variant="outline"
            >
              Save Draft
            </Button> */}
            <Button
              onClick={() => handleSaveRequest(true)}
              loading={saving}
              leftSection={<IconCheck size={16} />}
            >
              Submit for Approval
            </Button>
          </Group>
        )}
      </Group>

      {/* Record Selection */}
      {availableRecords.length > 1 && (
        <Card withBorder mb="md">
          <Select
            label={`${schemaLabel} Record Type`}
            description={`Select the type of ${schemaLabel} record to fill out`}
            value={selectedRecord}
            onChange={handleRecordChange}
            data={(() => {
              const selectData = availableRecords
                .filter(record => record && record.record) // Filter out any undefined records
                .map(record => ({
                  value: record.record || '',
                  label: getRecordDisplayLabel(record),
                }))
                .filter(item => item.value && item.label); // Ensure both value and label exist

              console.log('Select data:', selectData);
              return selectData;
            })()}
            leftSection={(() => {
              const record = selectedRecord ? availableRecords.find(r => r.record === selectedRecord) : null;
              if (!record || !record.isVirtual) return undefined;
              return record.isMultiRecord ? <IconLayersLinked size={16} /> : <IconCopy size={16} />;
            })()}
          />
        </Card>
      )}

      {/* Form */}
      {selectedRecord && (() => {
        const selectedRecordData = availableRecords.find(r => r.record === selectedRecord)!;

        // For multi-record forms, we need to handle them differently
        if (selectedRecordData.isMultiRecord && selectedRecordData.baseRecords && template?.virtualRecords?.[selectedRecord]) {
          const virtualRecord = template.virtualRecords[selectedRecord];

          return (
            <Stack gap="md">
              {/* <Alert color="violet" variant="light">
                <Text size="sm">
                  This is a multi-record form containing {selectedRecordData.baseRecords.length} records.
                  Fill out all sections below to create multiple records in a single submission.
                </Text>
              </Alert> */}

              {virtualRecord.multiRecordConfig?.map((config, index) => {
                const baseRecord = schema?.records.find(r => r.record === config.recordName);
                if (!baseRecord) return null;

                // Create a record with metadata for the form
                const recordForForm: RecordWithMetadata = {
                  ...baseRecord,
                  record: config.recordName,
                  enabled: true,
                  fieldCount: baseRecord.fields.length,
                  overriddenFieldCount: Object.keys(config.fieldOverrides || {}).length,
                  isVirtual: false, // Treat as non-virtual for form rendering
                };

                // Resolve field overrides properly using the multi-record resolver
                const resolvedOverrides = resolveMultiRecordFieldOverrides(
                  selectedRecord,
                  config.recordName,
                  template!
                );

                return (
                  <Card key={config.recordName} withBorder>
                    <Card.Section withBorder inheritPadding py="xs">
                      <Group gap="xs">
                        <Text fw={600}>{config.recordName}</Text>
                        <Badge size="sm" variant="outline">Record {index + 1}</Badge>
                      </Group>
                    </Card.Section>

                    <Card.Section inheritPadding py="md">
                      <GenevaRecordForm
                        record={recordForForm}
                        template={{
                          ...template!,
                          // Use properly resolved overrides that include base record overrides
                          fieldOverrides: {
                            [config.recordName]: resolvedOverrides,
                          },
                        }}
                        formData={formData[`${selectedRecord}__${config.recordName}`] || {}}
                        onFieldChange={(fieldName: string, value: any) =>
                          handleFieldChange(`${selectedRecord}__${config.recordName}`, fieldName, value)
                        }
                        onSupportingDataChange={(fieldName: string, item: GenevaApiItem | null) =>
                          handleSupportingDataChange(`${selectedRecord}__${config.recordName}`, fieldName, item)
                        }
                        readonly={readonly}
                      />
                    </Card.Section>
                  </Card>
                );
              })}
            </Stack>
          );
        }

        // Regular single record form
        const extras: any[] = Array.isArray(formData._multiTransactions) ? formData._multiTransactions : [];
        const recordDef = schema?.records.find(r => r.record === selectedRecord);
        const resolvedOverrides: Record<string, any> = template
          ? resolveFieldOverridesRecursively(selectedRecord, template)
          : {};
        const perTxnFields = (recordDef?.fields || []).filter(field => {
          const override = resolvedOverrides[field.xmlTag];
          if (override?.multiTxn !== true) {
            return false;
          }

          if (field.quickImportColumn && typeof field.quickImportColumn === 'string') {
            const firstLine = field.quickImportColumn.split('\n')[0].trim();
            if (firstLine.includes('.') && !firstLine.includes(' ') && !firstLine.startsWith('Note:')) {
              return false;
            }
          }

          return !field.xmlTag.includes('.');
        });
        const showAdditionalTransactions = perTxnFields.length > 0;

        return (
          <Stack gap="md">
            <Card withBorder>
              <GenevaRecordForm
                record={selectedRecordData}
                template={(() => {
                  // If it's a virtual record, use recursive inheritance resolution
                  if (selectedRecordData.isVirtual && selectedRecordData.baseRecord) {
                    const resolvedVirtualOverrides = resolveFieldOverridesRecursively(selectedRecord, template!);

                    return {
                      ...template!,
                      fieldOverrides: {
                        ...template!.fieldOverrides,
                        [selectedRecord]: resolvedVirtualOverrides,
                      },
                    };
                  }
                  return template!;
                })()}
                formData={formData[selectedRecord] || {}}
                onFieldChange={(fieldName: string, value: any) => handleFieldChange(selectedRecord, fieldName, value)}
                onSupportingDataChange={(fieldName: string, item: GenevaApiItem | null) =>
                  handleSupportingDataChange(selectedRecord, fieldName, item)
                }
                readonly={readonly}
              />
            </Card>

            {/* Additional Transactions Section */}
            {showAdditionalTransactions && (
              <Card withBorder>
                <Card.Section withBorder inheritPadding py="xs">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text fw={600}>Additional Transactions</Text>
                      <Badge variant="light" size="sm">
                        {extras.length} added
                      </Badge>
                    </Group>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => {
                        const arr = Array.isArray(formData._multiTransactions) ? formData._multiTransactions : [];
                        setFormData(prev => ({ ...prev, _multiTransactions: [...arr, {}] }));
                      }}
                      disabled={readonly}
                    >
                      Add Transaction
                    </Button>
                  </Group>
                </Card.Section>

                <Card.Section inheritPadding py="md">
                  {extras.length === 0 ? (
                    <Alert color="blue" variant="light">
                      <Text size="sm">Add one or more additional transactions that reuse the same portfolio data. Only the fields marked as per-transaction will be shown.</Text>
                    </Alert>
                  ) : (
                    <Stack gap="sm">
                      {extras.map((item, idx) => (
                        <Card key={idx} withBorder>
                          <Card.Section withBorder inheritPadding py="xs">
                            <Group justify="space-between">
                              <Group gap="xs">
                                <Text fw={600}>Transaction {idx + 2}</Text>
                                <Badge size="sm" variant="outline">Overrides</Badge>
                              </Group>
                              <Button
                                size="xs"
                                color="red"
                                variant="light"
                                onClick={() => {
                                  const arr = Array.isArray(formData._multiTransactions) ? [...formData._multiTransactions] : [];
                                  arr.splice(idx, 1);
                                  setFormData(prev => ({ ...prev, _multiTransactions: arr }));
                                }}
                                disabled={readonly}
                              >
                                Remove
                              </Button>
                            </Group>
                          </Card.Section>
                          <Card.Section inheritPadding py="md">
                            <Box
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: '1rem'
                              }}
                            >
                              {perTxnFields.map(field => {
                                const fieldWithOverride = {
                                  ...field,
                                  override: resolvedOverrides[field.xmlTag],
                                  isOverridden: !!resolvedOverrides[field.xmlTag] && Object.keys(resolvedOverrides[field.xmlTag]).length > 0,
                                } as any;
                                const value = item[field.xmlTag] ?? '';
                                return (
                                  <GenevaFieldRenderer
                                    key={`${idx}-${field.xmlTag}`}
                                    field={fieldWithOverride}
                                    value={value}
                                    onChange={(_, v) => {
                                      const arr = Array.isArray(formData._multiTransactions) ? [...formData._multiTransactions] : [];
                                      const next = { ...(arr[idx] || {}) };
                                      next[field.xmlTag] = v;
                                      arr[idx] = next;
                                      setFormData(prev => ({ ...prev, _multiTransactions: arr }));
                                    }}
                                    readOnly={readonly}
                                    required={false}
                                    showOverrideBadges={true}
                                  />
                                );
                              })}
                            </Box>
                          </Card.Section>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Card.Section>
              </Card>
            )}
          </Stack>
        );
      })()}
    </Box>
  );
}; 
